import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";

const CaseCreatorGuidelines = ({ open, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <InfoIcon color="primary" />
          Case Creation Best Practices
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Follow these guidelines to create realistic, effective cases for
          OSCE-style history taking practice.
        </Typography>

        {/* --- Writing Patient Narrative --- */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              <CheckCircleIcon sx={{ mr: 1, color: "success.main" }} />
              Writing the Patient Narrative
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" sx={{ mb: 2 }}>
              Use the Patient Narrative step to describe the case once. The AI will
              draw from this script and only reveal information when the student asks.
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="success.main">
                Recommended flow:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Open with why the patient sought help today and how they describe the problem in their own words." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Layer the history of the presenting complaint (onset, severity, red flags, modifiers) the way a real patient would recall it." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Blend in relevant past medical, drug, family, and social background so the dialogue feels natural." />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Capture ICE (ideas, concerns, expectations) so the AI can role-play genuine emotion and motivation." />
                </ListItem>
              </List>
            </Box>

            <Alert severity="warning">
              <Typography variant="subtitle2">Patient Notes are optional</Typography>
              <Typography variant="body2">
                Use Patient Notes inside the Mark Scheme only for examiner-only reminders, such as "only mention smoking if directly asked."
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>


        {/* --- Section Organization --- */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              <InfoIcon sx={{ mr: 1, color: "info.main" }} />
              Section Organization Tips
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Break down information into logical sections that mirror how a
              student would naturally take a history:
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Chip
                label="Presenting Complaint"
                color="primary"
                size="small"
                sx={{ mr: 1, mb: 1 }}
              />
              <Typography variant="caption">
                Keep this brief - just the main symptom
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Chip
                label="History of PC"
                color="primary"
                size="small"
                sx={{ mr: 1, mb: 1 }}
              />
              <Typography variant="caption">
                Details about the main symptom (timing, character, severity,
                etc.)
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Chip
                label="Associated Symptoms"
                color="secondary"
                size="small"
                sx={{ mr: 1, mb: 1 }}
              />
              <Typography variant="caption">
                Only mention if specifically asked "any other symptoms?"
              </Typography>
            </Box>

            <Alert severity="info">
              <Typography variant="body2">
                <strong>Pro Tip:</strong> Think about what questions a good
                medical student would ask, then create items for each of those
                questions.
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* --- Common Mistakes --- */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              <WarningIcon sx={{ mr: 1, color: "warning.main" }} />
              Common Mistakes to Avoid
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              <ListItem>
                <ListItemText
                  primary="Making the patient too forthcoming"
                  secondary={"Real patients don't volunteer everything. They answer what's asked."}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Using too much medical jargon"
                  secondary={
                    "Most patients don't say 'myocardial infarction' - they say 'heart attack' or 'chest pain'"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Inconsistent timeline"
                  secondary={
                    "Make sure all your timing information adds up (when symptoms started, when they got worse, etc.)"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Forgetting negative responses"
                  secondary={
                    "Include items for important negatives (e.g., 'No, no chest pain' for a respiratory case)"
                  }
                />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* --- Marking Criteria --- */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              <CheckCircleIcon sx={{ mr: 1, color: "success.main" }} />
              Marking Criteria Best Practices
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Write clear, specific marking criteria that focus on what the
              student should ask:
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="success.main">
                ✅ Good Marking Criteria:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    secondary={
                      "Student asks about the onset/timing of chest pain (2 marks for specific time, 1 mark for approximate timing)"
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    secondary={
                      "Student elicits the character/quality of the pain (2 marks for detailed description, 1 mark for basic character)"
                    }
                  />
                </ListItem>
              </List>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="error.main">
                ❌ Vague Marking Criteria:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    secondary={
                      "Student asks about chest pain (too vague - asks what specifically?)"
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    secondary={"Student takes good history (not measurable)"}
                  />
                </ListItem>
              </List>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* --- Personality Guidelines --- */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Character Personality Guidelines</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Use personality settings to make cases more realistic and
              challenging:
            </Typography>

            <List dense>
              <ListItem>
                <ListItemText
                  primary="Anxious patients"
                  secondary={
                    "May need reassurance before answering, might focus on worries"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Elderly patients"
                  secondary={
                    "May need questions repeated, might go off on tangents"
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Defensive patients"
                  secondary={
                    "Might be reluctant to discuss lifestyle factors (smoking, drinking)"
                  }
                />
              </ListItem>
            </List>

            <Alert severity="info">
              <Typography variant="body2">
                <strong>Remember:</strong> Even with different personalities,
                patients should still provide the required information when
                appropriately asked.
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Got it, thanks!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaseCreatorGuidelines;
